import Highcharts from '~/scripts/integrations/highcharts';

class WorkplaceSafetyCalculator {
  constructor() {
    this.form = document.getElementById('calculator-form');
    this.employeeCountInput = document.getElementById('employee-count');
    this.challengesInput = document.getElementById('challenges');
    this.recognitionsInput = document.getElementById('recognitions');
    this.nominationsInput = document.getElementById('nominations');
    this.challengeReductionInput = document.getElementById('challenge-reduction');
    this.recognitionReductionInput = document.getElementById('recognition-reduction');
    this.nominationReductionInput = document.getElementById('nomination-reduction');
    this.savingsAmount = document.getElementById('savings-amount');
    this.savingsText = document.getElementById('savings-text');

    // Chart instance
    this.safetySavingsChart = null;

    this.bindEvents();
    this.calculateSavings();
  }

  bindEvents() {
    [
      this.employeeCountInput,
      this.challengesInput,
      this.recognitionsInput,
      this.nominationsInput,
      this.challengeReductionInput,
      this.recognitionReductionInput,
      this.nominationReductionInput
    ].forEach(input => {
      input.addEventListener('input', () => this.calculateSavings());
      input.addEventListener('change', () => this.calculateSavings());
    });
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  updateSafetySavingsChart(challengeSavings, recognitionSavings, nominationSavings, reportingSavings, engagementSavings, totalSavings) {
    const chartData = [
      {
        name: 'Challenge Savings',
        y: challengeSavings,
        color: '#28a745'
      },
      {
        name: 'Recognition Savings',
        y: recognitionSavings,
        color: '#17a2b8'
      },
      {
        name: 'Nomination Savings',
        y: nominationSavings,
        color: '#ffc107'
      },
      {
        name: 'Reporting Increase',
        y: reportingSavings,
        color: '#6f42c1'
      },
      {
        name: 'Engagement Bonus',
        y: engagementSavings,
        color: '#fd7e14'
      }
    ];

    if (this.safetySavingsChart) {
      this.safetySavingsChart.destroy();
    }

    this.safetySavingsChart = Highcharts.chart('safety-savings-chart', {
      chart: {
        type: 'column',
        height: 400
      },
      title: {
        text: 'Annual Safety Savings Breakdown'
      },
      xAxis: {
        type: 'category',
        labels: {
          rotation: -45,
          style: {
            fontSize: '12px'
          }
        }
      },
      yAxis: {
        title: {
          text: 'Savings ($)'
        },
        labels: {
          formatter: function() {
            return '$' + (this.value / 1000).toFixed(0) + 'K';
          }
        }
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b><br/>' +
                    'Savings: {point.y:,.0f}'
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            formatter: function() {
              return '$' + (this.y / 1000).toFixed(0) + 'K';
            }
          }
        }
      },
      series: [{
        name: 'Annual Savings',
        data: chartData
      }],
      legend: {
        enabled: false
      }
    });
  }

  calculateSavings() {
    const employeeCount = parseFloat(this.employeeCountInput.value) || 0;
    const challenges = parseFloat(this.challengesInput.value) || 0;
    const recognitions = parseFloat(this.recognitionsInput.value) || 0;
    const nominations = parseFloat(this.nominationsInput.value) || 0;
    const challengeReductionPercent = parseFloat(this.challengeReductionInput.value) || 0;
    const recognitionReductionPercent = parseFloat(this.recognitionReductionInput.value) || 0;
    const nominationReductionPercent = parseFloat(this.nominationReductionInput.value) || 0;

    // Industry average safety costs per employee per year
    const avgSafetyCostPerEmployee = 1200; // Based on industry data
    const annualCost = employeeCount * avgSafetyCostPerEmployee;
    
    // Calculate reduction percentages based on program activities
    const challengeReduction = Math.floor(challenges / 100) * (challengeReductionPercent / 100); // Custom % per 100 challenges
    const recognitionReduction = Math.floor(recognitions / 50) * (recognitionReductionPercent / 100); // Custom % per 50 recognitions
    const nominationReduction = Math.floor(nominations / 25) * (nominationReductionPercent / 100); // Custom % per 25 nominations
    
    // Additional benefits from increased engagement
    const reportingIncrease = 0.30; // 30% increase in reporting
    const engagementBonus = 0.15; // 15% additional reduction from increased engagement
    
    // Calculate total reduction percentage
    const totalReduction = challengeReduction + recognitionReduction + nominationReduction + engagementBonus;
    
    // Calculate savings
    const totalSavings = annualCost * totalReduction;
    
    // Calculate individual savings components for chart
    const challengeSavings = annualCost * challengeReduction;
    const recognitionSavings = annualCost * recognitionReduction;
    const nominationSavings = annualCost * nominationReduction;
    const reportingSavings = annualCost * reportingIncrease * 0.1; // 10% of reporting increase
    const engagementSavings = annualCost * engagementBonus;

    // Update display
    this.savingsAmount.textContent = this.formatCurrency(totalSavings);
    this.savingsText.innerHTML = `
      Based on ${(totalReduction * 100).toFixed(1)}% reduction from program activities
      plus additional savings from ${Math.round(reportingIncrease * 100)}% increased reporting rate.
      <br><small class="text-muted">Estimated annual safety cost: ${this.formatCurrency(annualCost)}</small>
    `;

    // Update chart
    this.updateSafetySavingsChart(challengeSavings, recognitionSavings, nominationSavings, reportingSavings, engagementSavings, totalSavings);
  }
}

export default WorkplaceSafetyCalculator;
